import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Clock } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";

const DAYS = [1, 2, 3, 5, 7, 10, 14];
const HOURS = [[24, "1 day"], [48, "2 days"], [72, "3 days"], [120, "5 days"], [168, "1 week"]];

const Chips = ({ options, value, onChange, testPrefix, disabled }) => (
  <div className="flex flex-wrap gap-1.5 mt-2">
    {options.map(([v, label]) => (
      <button key={v} type="button" disabled={disabled} onClick={() => onChange(v)} data-testid={`${testPrefix}-${v}`}
        className={`px-3 py-1.5 rounded-full font-heading font-bold text-xs border transition-colors disabled:opacity-40 ${value === v ? "bg-primary text-white border-primary" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>
        {label}
      </button>
    ))}
  </div>
);

export const ReminderSettingsDialog = () => {
  const { user, setUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);

  const init = () => setF({
    reminder_days: user?.reminder_days || 3, reminder_enabled: user?.reminder_enabled !== false,
    nudge_hours: user?.nudge_hours || 48, nudge_enabled: user?.nudge_enabled !== false,
  });

  const save = async () => {
    setBusy(true);
    try {
      const res = await api.put("/admin/profile", f);
      setUser(res.data);
      toast.success("Reminder schedule saved");
      setOpen(false);
    } catch (e) { const d = e.response?.data?.detail; toast.error(typeof d === "string" ? d : "Could not save"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) init(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-xl font-heading font-bold" data-testid="reminder-settings-btn">
          <Clock className="w-4 h-4 mr-1" /> Reminders
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="reminder-settings-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading font-extrabold">Reminder schedule</DialogTitle>
          <DialogDescription className="font-body">Choose when your candidates get automatic email reminders. Applies to all codes you generate.</DialogDescription>
        </DialogHeader>
        <div className="space-y-6 mt-2">
          <div className="rounded-xl border border-slate-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="font-heading font-bold text-slate-900">Expiry reminder</Label>
                <p className="font-body text-xs text-slate-500 mt-0.5">Nudge candidates who haven't started before their code expires.</p>
              </div>
              <Switch checked={!!f.reminder_enabled} onCheckedChange={(v) => setF((s) => ({ ...s, reminder_enabled: v }))} data-testid="reminder-enabled-switch" />
            </div>
            <p className="font-body text-xs text-slate-600 mt-3">Send <span className="font-semibold">{f.reminder_days} day{f.reminder_days === 1 ? "" : "s"}</span> before expiry</p>
            <Chips options={DAYS.map((d) => [d, `${d}d`])} value={f.reminder_days} disabled={!f.reminder_enabled} testPrefix="reminder-days" onChange={(v) => setF((s) => ({ ...s, reminder_days: v }))} />
          </div>
          <div className="rounded-xl border border-slate-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="font-heading font-bold text-slate-900">"Halfway there" nudge</Label>
                <p className="font-body text-xs text-slate-500 mt-0.5">Email candidates who started but stopped mid-assessment.</p>
              </div>
              <Switch checked={!!f.nudge_enabled} onCheckedChange={(v) => setF((s) => ({ ...s, nudge_enabled: v }))} data-testid="nudge-enabled-switch" />
            </div>
            <p className="font-body text-xs text-slate-600 mt-3">After <span className="font-semibold">{HOURS.find(([h]) => h === f.nudge_hours)?.[1] || `${f.nudge_hours}h`}</span> of inactivity</p>
            <Chips options={HOURS} value={f.nudge_hours} disabled={!f.nudge_enabled} testPrefix="nudge-hours" onChange={(v) => setF((s) => ({ ...s, nudge_hours: v }))} />
          </div>
          <DialogFooter>
            <Button onClick={save} disabled={busy} className="rounded-xl font-heading font-bold w-full" data-testid="reminder-save-btn">
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} Save schedule
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};
